/**
 * 用途：401 跳登录前暂存原路由，登录成功后回跳（HashRouter 场景）。
 * 输入：window.location 的 pathname + search + hash，写入 sessionStorage。
 * 输出：consumePostLoginRedirect() 完成跳转并清除 key，返回是否发生回跳。
 */

const POST_LOGIN_REDIRECT_KEY = 'post_login_redirect'
const LOGIN_ROUTE = '/login'

function isLoginRoute(hash: string): boolean {
  const route = hash.replace(/^#/, '')
  return route === LOGIN_ROUTE || route.startsWith(`${LOGIN_ROUTE}?`)
}

/** 401 跳登录前调用：只存站内路径，登录页自身不存，避免回跳成环。 */
export function savePostLoginRedirect(): void {
  try {
    if (isLoginRoute(window.location.hash)) return
    const target = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (!target) return
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, target)
  } catch {
    // sessionStorage 不可用（隐私模式/配额）时降级为默认首页，不阻断登录流程
  }
}

/**
 * 登录成功后调用：读取 → 跳转 → 清除 key。
 * 返回 true 表示已按暂存路由回跳；false 表示没有可用的暂存路由。
 */
export function consumePostLoginRedirect(): boolean {
  let target: string | null = null
  try {
    target = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
    if (target) sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
  } catch {
    return false
  }
  if (!target || !target.startsWith('/') || target.startsWith('//')) return false

  let url: URL
  try {
    url = new URL(target, window.location.origin)
  } catch {
    return false
  }
  // 只允许同源地址，防止 sessionStorage 被写入外部链接后形成开放重定向
  if (url.origin !== window.location.origin) return false
  if (!url.hash || isLoginRoute(url.hash)) return false

  const targetPath = `${url.pathname}${url.search}`
  const currentPath = `${window.location.pathname}${window.location.search}`
  if (targetPath !== currentPath) {
    // 部署在子路径等跨 pathname 场景：整页跳转，保证落到目标文档
    window.location.replace(`${targetPath}${url.hash}`)
    return true
  }

  // HashRouter 的路由在 hash 内；改 hash 后补发 popstate，确保 Router 立即感知
  window.location.hash = url.hash
  if (typeof PopStateEvent !== 'undefined') {
    window.dispatchEvent(new PopStateEvent('popstate'))
  }
  return true
}
